import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class IndexedDBCacheService {
  private readonly DB_NAME = 'masofa_cache_v1';
  private readonly STORE_NAME = 'kv';

  private openIndexedDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(this.DB_NAME, 1);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(this.STORE_NAME)) {
            db.createObjectStore(this.STORE_NAME);
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } catch (err) {
        reject(err);
      }
    });
  }

  async get(key: string): Promise<string | null> {
    // Try IndexedDB first, fallback to localStorage
    try {
      const db = await this.openIndexedDB();
      return await new Promise<string | null>((resolve) => {
        try {
          const tx = db.transaction(this.STORE_NAME, 'readonly');
          const store = tx.objectStore(this.STORE_NAME);
          const req = store.get(key);
          req.onsuccess = () => {
            const val = req.result;
            if (val === undefined) return resolve(null);
            resolve(typeof val === 'string' ? val : JSON.stringify(val));
          };
          req.onerror = () => resolve(null);
        } catch (e) {
          resolve(null);
        }
      });
    } catch (e) {
      // Fallback to localStorage if IndexedDB is not available or fails
      try {
        if (typeof localStorage !== 'undefined') {
          return localStorage.getItem(key);
        }
      } catch (err) {
        // ignore
      }
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      const db = await this.openIndexedDB();
      await new Promise<void>((resolve) => {
        try {
          const tx = db.transaction(this.STORE_NAME, 'readwrite');
          const store = tx.objectStore(this.STORE_NAME);
          const req = store.put(value, key);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
        } catch (e) {
          resolve();
        }
      });
    } catch (e) {
      // Fallback to localStorage
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(key, value);
        }
      } catch (err) {
        // ignore
      }
    }
  }

  /**
   * Compare the size (length) of new data with cached data
   * Returns true if sizes are different
   */
  async hasDataSizeChanged(key: string, newData: string): Promise<boolean> {
    try {
      const cachedData = await this.get(key);
      if (!cachedData) return true; // No cache exists, consider it changed
      return cachedData.length !== newData.length;
    } catch (e) {
      return true; // On error, consider it changed
    }
  }

  async clear(key: string): Promise<void> {
    try {
      const db = await this.openIndexedDB();
      await new Promise<void>((resolve) => {
        try {
          const tx = db.transaction(this.STORE_NAME, 'readwrite');
          const store = tx.objectStore(this.STORE_NAME);
          const req = store.delete(key);
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
        } catch (e) {
          resolve();
        }
      });
    } catch (e) {
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(key);
        }
      } catch (_) {
        // ignore
      }
    }
  }

  async clearAll(): Promise<void> {
    try {
      const db = await this.openIndexedDB();
      await new Promise<void>((resolve) => {
        try {
          const tx = db.transaction(this.STORE_NAME, 'readwrite');
          const store = tx.objectStore(this.STORE_NAME);
          const req = store.clear();
          req.onsuccess = () => resolve();
          req.onerror = () => resolve();
        } catch (e) {
          resolve();
        }
      });
    } catch (e) {
      // ignore
    }
  }
}
